import api from "./api";

const CEREMONY_TIMEOUT_MS = 60 * 1000;

// True only when launched as an installed PWA (Add to Home Screen), not a browser tab.
export function isInstalledPWA() {
  if (typeof window === "undefined") return false;
  const mm = (q) => window.matchMedia && window.matchMedia(q).matches;
  return (
    mm("(display-mode: standalone)") ||
    mm("(display-mode: fullscreen)") ||
    mm("(display-mode: minimal-ui)") ||
    window.navigator.standalone === true // iOS Safari
  );
}

export function webauthnSupported() {
  return typeof window !== "undefined" && !!window.PublicKeyCredential && !!(navigator.credentials && navigator.credentials.create);
}

// True only on a phone/tablet (not desktop). iPadOS reports as Macintosh but is touch-capable.
export function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const uaData = navigator.userAgentData;
  if (uaData && typeof uaData.mobile === "boolean" && uaData.mobile) return true;
  if (/Android|iPhone|iPod|Windows Phone/i.test(ua)) return true;
  if (/iPad/i.test(ua)) return true;
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return true;
  return false;
}

// Is a built-in biometric / screen-lock authenticator actually usable on this device?
export async function platformAuthAvailable() {
  try {
    if (!webauthnSupported() || !window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// The reverse proxy can strip/rewrite the Origin header, so we pass the real page
// origin in a custom header; the backend validates it against its server-side allowlist.
function cfg() {
  return { headers: { "X-Webauthn-Origin": window.location.origin } };
}

function b64uToBytes(b64) {
  const s = b64.replace(/-/g, "+").replace(/_/g, "/");
  const pad = s + "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(pad);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

function bytesToB64u(buf) {
  const bin = String.fromCharCode(...new Uint8Array(buf));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

// Run a WebAuthn ceremony with a hard timeout so navigator.credentials.{get,create}
// can never hang forever (Android Credential Manager can leave the promise pending).
// A timeout aborts the ceremony and surfaces a retryable TIMEOUT error (not a cancel).
async function withTimeout(runCeremony) {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, CEREMONY_TIMEOUT_MS);
  try {
    return await runCeremony(controller.signal);
  } catch (e) {
    if (timedOut) {
      const err = new Error("Timed out. Please try again.");
      err.code = "TIMEOUT";
      throw err;
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

export async function getStatus() {
  const { data } = await api.get("/webauthn/status", cfg());
  return data; // { enrolled: bool }
}

export async function registerDevice() {
  if (!webauthnSupported()) throw new Error("This device doesn't support secure unlock.");
  if (!(await platformAuthAvailable())) {
    const e = new Error("No platform authenticator available");
    e.code = "NO_AUTHENTICATOR";
    throw e;
  }
  const { data: o } = await api.post("/webauthn/registration/begin", {}, cfg());
  const publicKey = {
    ...o,
    challenge: b64uToBytes(o.challenge),
    user: { ...o.user, id: b64uToBytes(o.user.id) },
    excludeCredentials: (o.excludeCredentials || []).map((c) => ({ ...c, id: b64uToBytes(c.id) })),
  };
  const cred = await withTimeout((signal) => navigator.credentials.create({ publicKey, signal }));
  if (!cred) throw new Error("Setup cancelled");
  const r = cred.response;
  await api.post("/webauthn/registration/finish", {
    id: cred.id,
    rawId: bytesToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bytesToB64u(r.clientDataJSON),
      attestationObject: bytesToB64u(r.attestationObject),
      transports: r.getTransports ? r.getTransports() : ["internal"],
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  }, cfg());
  return true;
}

export async function unlockDevice() {
  if (!(await platformAuthAvailable())) {
    const e = new Error("No platform authenticator available");
    e.code = "NO_AUTHENTICATOR";
    throw e;
  }
  const { data: o } = await api.post("/webauthn/authentication/begin", {}, cfg());
  const publicKey = {
    ...o,
    challenge: b64uToBytes(o.challenge),
    // Keep the server-provided transports (e.g. ["internal"]) so Android Credential Manager
    // surfaces the platform passkey; dropping them can leave get() pending forever.
    allowCredentials: (o.allowCredentials || []).map((c) => ({
      type: c.type || "public-key",
      id: b64uToBytes(c.id),
      transports: c.transports || ["internal"],
    })),
  };
  const cred = await withTimeout((signal) => navigator.credentials.get({ publicKey, signal }));
  if (!cred) throw new Error("Unlock cancelled");
  const r = cred.response;
  await api.post("/webauthn/authentication/finish", {
    id: cred.id,
    rawId: bytesToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bytesToB64u(r.clientDataJSON),
      authenticatorData: bytesToB64u(r.authenticatorData),
      signature: bytesToB64u(r.signature),
      userHandle: r.userHandle ? bytesToB64u(r.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  }, cfg());
  return true;
}

// Tell the server this installed-PWA session is locked, so the lock is enforced server-side
// (not just in the UI). Best-effort — never throws.
export async function lockDevice() {
  try { await api.post("/webauthn/lock"); } catch (e) { /* noop */ }
}

// A ceremony timeout aborts via our controller (AbortError) but is retryable, not a real cancel.
export function isTimeout(err) {
  return err && err.code === "TIMEOUT";
}

// A genuine user cancel / no matching passkey. Note: our timeout also throws AbortError, so
// check isTimeout() first — here we only treat NotAllowedError as a user-driven cancel.
export function isCancelled(err) {
  return err && err.name === "NotAllowedError";
}
